<template>
  <div class="dollar-value-outer">
    <div class="value-row">
      <div
        :style="{
          color: props.color,
          fontSize: props.fontSize,
          fontWeight: 700,
          lineHeight: props.lineHeight
        }"
      >
        {{ formatCurrency(props.value, currentPrivacyMode === 'yes' && props.privacy) }}
      </div>
      <div
        v-if="$slots.info"
        ref="infoWrapperRef"
        class="info-wrapper"
        @mouseenter="openTooltip"
        @mouseleave="closeTooltip"
      >
        <InfoIcon
          class="info-icon"
        />
        <Teleport to="body">
          <div
            v-if="showTooltip"
            class="info-tooltip"
            :style="{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: 'translateX(-50%)'
            }"
            @mouseenter="openTooltip"
            @mouseleave="closeTooltip"
          >
            <slot name="info" />
          </div>
        </Teleport>
      </div>
    </div>
    <slot name="caption" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { InfoIcon } from '@/components/icons'

import { usePrivacyStore } from '@/stores'
import { formatCurrency } from './common'

interface IProps {
  value: number
  color?: string
  fontSize: string
  lineHeight?: string
  privacy?: boolean
  infoColor?: string
}

const props = defineProps<IProps>()

const privacyStore = usePrivacyStore()

const { currentPrivacyMode } = storeToRefs(privacyStore)

const infoWrapperRef = ref<HTMLElement | null>(null)
const showTooltip = ref(false)
const tooltipPosition = ref({ top: 0, left: 0 })
const isHovering = ref(false)

const updateTooltipPosition = () => {
  const el = infoWrapperRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  tooltipPosition.value = {
    top: rect.bottom + 8,
    left: rect.left + rect.width / 2
  }
}

const openTooltip = () => {
  isHovering.value = true
  updateTooltipPosition()
  showTooltip.value = true
}

const closeTooltip = () => {
  isHovering.value = false
  showTooltip.value = false
}

const handleScrollOrResize = () => {
  if (!showTooltip.value) return
  if (!isHovering.value) {
    closeTooltip()
  } else {
    updateTooltipPosition()
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScrollOrResize, { passive: true })
  window.addEventListener('resize', handleScrollOrResize)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScrollOrResize)
  window.removeEventListener('resize', handleScrollOrResize)
})
</script>

<style scoped>
.dollar-value-outer {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.value-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.info-icon {
  font-size: var(--font-size-h3);
  color: var(--text-color);
  opacity: 0.5;
}

.info-tooltip {
  position: fixed;
  background: var(--color-background-bright);
  color: var(--text-color);
  border: 0.0625rem solid var(--color-background);
  border-radius: 0.625rem;
  padding: 0.5rem 0.625rem;
  width: fit-content;
  min-width: 12.5rem;
  max-width: 20rem;
  white-space: normal;
  word-break: normal;
  font-size: 0.75rem;
  line-height: 1.4;
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.08);
  z-index: 1000;
  text-align: left;
}
</style>
